import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { VirtuosoHandle } from "react-virtuoso";
import { v4 as uuid } from "uuid";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import isEmpty from "../../common/helpers/isEmpty";
import { Nullable } from "../../common/types/nullable";
import useMessage from "../../hooks/useMessage";
import { toMessageAttachments } from "./attachments/messageAttachments";
import { uploadConversationAttachments } from "./attachments/uploadConversationAttachments";
import {
  getPendingComposerFiles,
  pendingComposerKey,
  setPendingComposerFiles,
} from "./attachments/pendingComposerFiles";
import { takeAcceptedAttachmentFiles } from "./attachments/validateFiles";
import {
  CloseConversationOptions,
  getClosingMessageToSend,
} from "./conversationClosingMessage";
import {
  normalizeConversationTitle,
  resolveConversationTitle,
} from "./conversation/conversationTitle";
import {
  canReopenClosedConversation,
  getCreateConversationType,
  isCustomerConversation,
} from "./helpers";
import { shouldMarkConversationRead } from "./readReceipt";
import {
  assignConversationToMe,
  createNewConversation,
  createNewConversationMessage,
  fetchConversationMessages,
  fetchInboxSummary,
  markMessagesRead,
  transferConversation,
  updateConvStatus,
} from "./messagingActions";
import {
  clearOutgoingDraft,
  selectAllMessages,
  selectComposerDraft,
  selectMessagesLoading,
  selectMessagesLoadingMore,
  selectMessagesNextToken,
  selectMyTeamIds,
  selectOutgoingTitle,
  selectSelectedConversation,
  selectTopicsById,
  setComposerDraft,
  setOutgoingTitle,
  setSelectedConversationId,
} from "./messagingSlice";
import {
  canTransferConversation,
  canTransferToTeam,
  toTransferConversationCommand,
  TransferConversationOptions,
} from "./transferConversation";
import { ParticipantOption } from "./types/common";
import { ConversationStatus } from "./types/queries";

type Options = {
  participant: Nullable<ParticipantOption>;
  onAssignedToMe?: () => void;
};

const useMessagingThread = ({ participant, onAssignedToMe }: Options) => {
  const { t, i18n } = useTranslation("messaging");
  const { sendMessage } = useMessage();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [isAssigning, setIsAssigning] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const mountedRef = useRef(true);
  const sendingRef = useRef(false);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const isInitialRender = useRef(true);
  const isUpdatingRef = useRef(false);
  const sentClosingMessageForRef = useRef<string | null>(null);

  const selectedConversation = useAppSelector(selectSelectedConversation);
  const topicsById = useAppSelector(selectTopicsById);
  const selectedConversationId = selectedConversation?.id;
  const selectedConversationIdRef = useRef(selectedConversationId);
  selectedConversationIdRef.current = selectedConversationId;
  const pendingKey = pendingComposerKey(selectedConversationId);
  const [pendingFiles, setPendingFilesState] = useState<File[]>(() =>
    getPendingComposerFiles(pendingKey),
  );

  const setPendingFiles = useCallback(
    (update: File[] | ((current: File[]) => File[])) => {
      setPendingFilesState((current) => {
        const next = typeof update === "function" ? update(current) : update;
        setPendingComposerFiles(pendingKey, next);
        return next;
      });
    },
    [pendingKey],
  );
  const selectedConversationStatus = selectedConversation?.status;
  const draft = useAppSelector((state) =>
    selectComposerDraft(state, pendingKey),
  );
  const outgoingTitle = useAppSelector(selectOutgoingTitle);
  const user = useAppSelector((state) => state.user.user);
  const userId = user?.id;
  const myTeamIds = useAppSelector(selectMyTeamIds);

  const messagesNextToken = useAppSelector((state) =>
    selectMessagesNextToken(state, selectedConversationId),
  );
  const messagesLoading = useAppSelector((state) =>
    selectMessagesLoading(state, selectedConversationId),
  );
  const selectedMessagesIdle = useAppSelector(
    (state) =>
      !!selectedConversationId &&
      state.messaging.messagesByConversation[selectedConversationId]?.status ===
        "idle",
  );
  const messagesLoadingMore = useAppSelector((state) =>
    selectMessagesLoadingMore(state, selectedConversationId),
  );
  const messages = useAppSelector((state) =>
    selectAllMessages(state, selectedConversationId),
  );

  const dispatchFetchConversationMessages = useCallback(
    (nextToken?: string) => {
      if (!selectedConversationId) return;
      dispatch(
        fetchConversationMessages({
          conversationId: selectedConversationId,
          ...(nextToken && { nextToken }),
          ...(!nextToken && { isInitial: true }),
        }),
      );
    },
    [dispatch, selectedConversationId],
  );

  const loadMore = useCallback(() => {
    if (messagesLoadingMore || !messagesNextToken) return;
    dispatchFetchConversationMessages(messagesNextToken);
  }, [
    dispatchFetchConversationMessages,
    messagesLoadingMore,
    messagesNextToken,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    isInitialRender.current = true;
    sentClosingMessageForRef.current = null;
    setPendingFilesState(getPendingComposerFiles(pendingKey));
  }, [pendingKey]);

  const addPendingFiles = useCallback(
    (files: File[]) => {
      const { accepted, rejected } = takeAcceptedAttachmentFiles(
        files,
        pendingFiles.length,
      );
      const reasons = [...new Set(rejected.map((item) => item.reason))];
      if (reasons.includes("tooLarge")) {
        sendMessage(t("attachmentFileTooLarge"), "error");
      }
      if (reasons.includes("empty")) {
        sendMessage(t("attachmentFileEmpty"), "error");
      }
      if (reasons.includes("unsupportedType")) {
        sendMessage(t("attachmentTypeNotSupported"), "error");
      }
      if (reasons.includes("tooMany")) {
        sendMessage(t("attachmentLimitReached"), "error");
      }
      if (accepted.length) {
        setPendingFiles((current) => [...current, ...accepted]);
      }
    },
    [pendingFiles.length, sendMessage, setPendingFiles, t],
  );

  const removePendingFile = useCallback(
    (index: number) => {
      setPendingFiles((current) => current.filter((_, i) => i !== index));
    },
    [setPendingFiles],
  );

  useEffect(() => {
    if (messages.length > 0 && isInitialRender.current) {
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({
          index: "LAST",
          align: "end",
          behavior: "auto",
        });
        isInitialRender.current = false;
      }, 0);
    }
  }, [messages]);

  useEffect(() => {
    if (
      !messagesLoading &&
      selectedConversationStatus !== ConversationStatus.DRAFT
    ) {
      dispatchFetchConversationMessages();
    }
    // messagesLoading blocks only an in-flight fetch. selectedMessagesIdle
    // retriggers when resetMessagesByConvId marks the open thread idle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchFetchConversationMessages, selectedMessagesIdle]);

  useEffect(() => {
    const conversationId = selectedConversation?.id;
    const lastMessage = selectedConversation?.lastMessage;
    if (
      !conversationId ||
      !lastMessage ||
      !shouldMarkConversationRead({
        conversationId,
        lastMessageId: lastMessage.id,
        lastMessageSenderId: lastMessage.senderId,
        userId,
      })
    ) {
      return;
    }

    dispatch(
      markMessagesRead({
        conversationId,
        lastReadMessageId: lastMessage.id,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        dispatch(fetchInboxSummary());
      }
    });
    // Only id and senderId decide whether to mark the thread read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    selectedConversation?.id,
    selectedConversation?.lastMessage?.id,
    selectedConversation?.lastMessage?.senderId,
    userId,
  ]);

  const assignToMe = useCallback(() => {
    if (!selectedConversationId || isUpdatingRef.current) return;
    if (
      selectedConversationStatus === ConversationStatus.CLOSED &&
      !canReopenClosedConversation(participant)
    ) {
      return;
    }
    isUpdatingRef.current = true;
    setIsAssigning(true);

    const result =
      selectedConversationStatus === ConversationStatus.CLOSED
        ? dispatch(
            updateConvStatus({
              conversationId: selectedConversationId,
              status: ConversationStatus.IN_PROGRESS,
            }),
          )
        : dispatch(
            assignConversationToMe({ conversationId: selectedConversationId }),
          );

    return result
      .then((action) => {
        if (isFulfilled(action)) {
          dispatch(fetchInboxSummary());
          dispatch(setSelectedConversationId(selectedConversationId));
          onAssignedToMe?.();
        }
      })
      .finally(() => {
        isUpdatingRef.current = false;
        setIsAssigning(false);
      });
  }, [
    dispatch,
    onAssignedToMe,
    participant,
    selectedConversationId,
    selectedConversationStatus,
  ]);

  const transfer = useCallback(
    async ({
      destination,
      sendTransferMessage,
      language,
    }: TransferConversationOptions) => {
      const conversationId = selectedConversation?.id;
      if (!conversationId || isUpdatingRef.current) return;

      isUpdatingRef.current = true;
      setIsAssigning(true);
      try {
        const transferAction = await dispatch(
          transferConversation(
            toTransferConversationCommand(conversationId, {
              destination,
              sendTransferMessage,
              language,
            }),
          ),
        );
        if (isFulfilled(transferAction)) {
          dispatch(fetchInboxSummary());
        }
      } finally {
        isUpdatingRef.current = false;
        setIsAssigning(false);
      }
    },
    [dispatch, selectedConversation?.id],
  );

  const markAsDone = useCallback(
    async ({ sendClosingMessage, language }: CloseConversationOptions) => {
      const conversationId = selectedConversation?.id;
      if (!conversationId || isUpdatingRef.current) return;

      isUpdatingRef.current = true;
      setIsAssigning(true);
      try {
        if (!selectedConversation?.assigneeId) {
          const assignAction = await dispatch(
            assignConversationToMe({ conversationId }),
          );
          if (!isFulfilled(assignAction)) return;
          dispatch(fetchInboxSummary());
        }

        const closingMessage = getClosingMessageToSend({
          sendClosingMessage,
          language,
        });
        if (
          closingMessage &&
          sentClosingMessageForRef.current !== conversationId
        ) {
          const sendAction = await dispatch(
            createNewConversationMessage({
              id: uuid(),
              text: closingMessage,
              conversationId,
            }),
          );
          if (!isFulfilled(sendAction)) return;
          sentClosingMessageForRef.current = conversationId;
        }

        const closeAction = await dispatch(
          updateConvStatus({
            conversationId,
            status: ConversationStatus.CLOSED,
          }),
        );
        if (isFulfilled(closeAction)) {
          sentClosingMessageForRef.current = null;
          dispatch(fetchInboxSummary());
        }
      } finally {
        isUpdatingRef.current = false;
        setIsAssigning(false);
      }
    },
    [dispatch, selectedConversation],
  );

  const onDraftChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    dispatch(
      setComposerDraft({
        conversationId: pendingKey,
        text: event.target.value,
      }),
    );
  };

  const onTitleChange = (value: string) => {
    dispatch(setOutgoingTitle(value));
  };

  const canSendMessage = Boolean(draft.trim()) || pendingFiles.length > 0;

  const assignedToOtherUser = Boolean(
    selectedConversation?.assigneeId &&
      selectedConversation.assigneeId !== userId,
  );

  const conversationSendBlocked =
    selectedConversationStatus === ConversationStatus.OPEN ||
    selectedConversationStatus === ConversationStatus.CLOSED ||
    assignedToOtherUser;

  const customerConversation = isCustomerConversation({
    conversationType: selectedConversation?.type,
    participantTypes: [
      participant?.type,
      ...(selectedConversation?.participants.map((item) => item.type) ?? []),
    ],
  });

  const onSend = useCallback(() => {
    if (sendingRef.current) return;

    const text = draft;
    if (
      (!text.trim() && pendingFiles.length === 0) ||
      !participant ||
      !user ||
      !userId ||
      (selectedConversationId && conversationSendBlocked)
    ) {
      return;
    }

    const files = pendingFiles;
    const createdBeforeSend = !selectedConversationId;

    const sendMessageTo = (conversationId: string) => {
      const attachments = toMessageAttachments(files);
      const messageId = uuid();
      const sentAt = new Date().toISOString();

      setPendingComposerFiles(conversationId, []);
      setPendingFilesState([]);
      dispatch(setComposerDraft({ conversationId, text: "" }));

      return dispatch(
        createNewConversationMessage({
          id: messageId,
          text,
          conversationId,
          senderId: userId,
          sentAt,
          ...(attachments.length ? { attachments } : {}),
        }),
      ).then((action) => {
        if (isRejected(action)) {
          const currentDraft = selectComposerDraft(
            store.getState(),
            conversationId,
          );
          if (!currentDraft) {
            dispatch(setComposerDraft({ conversationId, text }));
          }
          if (
            files.length > 0 &&
            getPendingComposerFiles(conversationId).length === 0
          ) {
            setPendingComposerFiles(conversationId, files);
            if (
              mountedRef.current &&
              selectedConversationIdRef.current === conversationId
            ) {
              setPendingFilesState(files);
            }
          }
          return;
        }

        if (!isFulfilled(action)) return;

        if (files.length) {
          void uploadConversationAttachments(dispatch, {
            conversationId,
            messageId,
            files,
            attachments,
          });
        }
        if (
          createdBeforeSend ||
          selectedConversationStatus === ConversationStatus.DRAFT ||
          selectedConversationStatus === ConversationStatus.OPEN
        ) {
          if (isUpdatingRef.current) return;
          isUpdatingRef.current = true;
          setIsAssigning(true);
          dispatch(assignConversationToMe({ conversationId }))
            .then((assignAction) => {
              if (isFulfilled(assignAction)) {
                dispatch(fetchInboxSummary());
                dispatch(setSelectedConversationId(conversationId));
                onAssignedToMe?.();
              }
            })
            .finally(() => {
              isUpdatingRef.current = false;
              setIsAssigning(false);
            });
        }
      });
    };

    sendingRef.current = true;

    const run = async () => {
      if (selectedConversationId) {
        await sendMessageTo(selectedConversationId);
        return;
      }

      setIsCreatingConversation(true);
      let conversationId: string | undefined;
      try {
        const topic = customerConversation
          ? undefined
          : normalizeConversationTitle(outgoingTitle);
        const action = await dispatch(
          createNewConversation({
            type: getCreateConversationType(user, participant.type),
            participantIds: [participant.id],
            otherParticipant: participant,
            ...(topic ? { topic } : {}),
          }),
        );
        if (isFulfilled(action)) {
          conversationId = action.payload.response.result.conversationId;
        }
      } finally {
        if (mountedRef.current) setIsCreatingConversation(false);
      }
      if (!conversationId) return;

      setPendingComposerFiles(pendingComposerKey(null), []);
      dispatch(setSelectedConversationId(conversationId));
      dispatch(clearOutgoingDraft());
      await sendMessageTo(conversationId);
    };

    void run().finally(() => {
      sendingRef.current = false;
    });
  }, [
    conversationSendBlocked,
    customerConversation,
    dispatch,
    draft,
    onAssignedToMe,
    outgoingTitle,
    participant,
    pendingFiles,
    selectedConversationId,
    selectedConversationStatus,
    store,
    user,
    userId,
  ]);

  const sendingDisabled =
    !canSendMessage ||
    !userId ||
    !participant ||
    isCreatingConversation ||
    conversationSendBlocked;

  const inputDisabled =
    !participant ||
    selectedConversationStatus === ConversationStatus.OPEN ||
    selectedConversationStatus === ConversationStatus.CLOSED ||
    assignedToOtherUser;

  const isComposingNewConversation =
    Boolean(participant) && !selectedConversationId;
  const resolvedTitle = resolveConversationTitle({
    topic: selectedConversation?.topic,
    topicId: selectedConversation?.topicId,
    topicsById,
    language: i18n.language,
  });
  const conversationTitle = customerConversation
    ? undefined
    : isComposingNewConversation
      ? outgoingTitle
      : resolvedTitle;
  const titleLocked = !isComposingNewConversation;

  const composerPlaceholder = !participant
    ? t("selectRecipient")
    : assignedToOtherUser
      ? t("composerAssignedToOtherPlaceholder")
      : selectedConversationStatus === ConversationStatus.OPEN
        ? t("composerOpenPlaceholder")
        : selectedConversationStatus === ConversationStatus.CLOSED
          ? t("composerClosedPlaceholder")
          : undefined;

  const isClosedConversation =
    selectedConversationStatus === ConversationStatus.CLOSED;
  const closedTalentActionsBlocked =
    isClosedConversation && !canReopenClosedConversation(participant);

  const canAssignConversation =
    !isEmpty(messages) &&
    selectedConversationStatus !== ConversationStatus.IN_PROGRESS &&
    !closedTalentActionsBlocked;

  const canMarkAsDone =
    selectedConversationStatus === ConversationStatus.IN_PROGRESS &&
    !isEmpty(messages) &&
    !isAssigning;

  const canTransfer =
    canTransferConversation({
      status: selectedConversationStatus,
      assigneeId: selectedConversation?.assigneeId,
      userId,
      hasMessages: !isEmpty(messages),
    }) && !closedTalentActionsBlocked;

  const teamTransferDisabled = !canTransferToTeam(
    selectedConversation,
    myTeamIds,
  );

  return {
    messages,
    messagesLoading: !!messagesLoading,
    messagesLoadingMore: !!messagesLoadingMore,
    loadMore,
    virtuosoRef,
    draft,
    onDraftChange,
    onTitleChange:
      isComposingNewConversation && !customerConversation
        ? onTitleChange
        : undefined,
    onSend,
    sendingDisabled,
    inputDisabled,
    pendingFiles,
    addPendingFiles,
    removePendingFile,
    composerPlaceholder,
    conversationTitle,
    titleLocked,
    isCreatingConversation,
    isAssigning,
    canAssignConversation,
    isClosedConversation,
    canMarkAsDone,
    canTransfer,
    teamTransferDisabled,
    assignToMe,
    markAsDone,
    transfer,
    selectedConversationId,
  };
};

export default useMessagingThread;
